import { useEffect, useState } from "react"
import type { Timeout } from "@/v2-common/types"
import { logError } from "@/v2-ui/utils/utils.log"

/**
 * Retrieves DOM elements that might be added to the document
 * at a later point in time from when the function was called.
 * Will automatically clean up after itself when the element
 * was found
 *
 * @param selector A querySelector string
 * @param cb A function that receives the element
 * @param timeout A max duration for how long it should look for element
 *                (the interval is 50ms, which allows for 10 interation
 *                with the default timeout of 500ms)
 *
 * @returns Timeout handler so that the interval can be cleared
 *          outside this function
 */
export function querySelectorLazy<T extends Element = HTMLElement>(
  selector: string,
  cb: (el: T) => void,
  timeout?: number,
): Timeout

export function querySelectorLazy<T extends Element = HTMLElement>(
  selector: string[],
  cb: (el: T[]) => void,
  timeout?: number,
): Timeout

export function querySelectorLazy<T extends Element = HTMLElement>(
  _selector: string | string[],
  cb: (el: T | T[]) => void,
  timeout = 500,
) {
  const interval = 50
  const selector = Array.isArray(_selector) ? [..._selector] : _selector

  let elapsed = 0

  const t = setInterval(() => {
    if (elapsed >= timeout) {
      clearInterval(t)
      return
    }

    if (Array.isArray(selector)) {
      const els = selector.map((sel) => document.querySelector<T>(sel))

      if (els.every((el): el is T => el != null)) {
        clearInterval(t)
        cb(els as T[])
      }
    } else {
      const el = document.querySelector<T>(selector)
      if (el) {
        clearInterval(t)
        cb(el)
      }
    }

    elapsed += interval
  }, interval)

  return t
}

/**
 * Resolves synchronously when the element already exists: starting at
 * undefined re-parents portals (body → container) one interval-tick after
 * every mount, which remounts the portaled subtree — an open/close/open
 * flash on dialogs that mount per open. The interval stays as the
 * late-container fallback only.
 *
 * The selector is reactive by VALUE: a changed string re-resolves (and a
 * changed portal container remounts its subtree — inherent to React
 * portals), while re-renders with the same string are free. Keep the
 * parameter a bare string; an array/object form would put a memoization
 * burden on every consumer.
 */
export function useQuerySelectorLazy<T extends Element = HTMLElement>(
  selector: string,
) {
  const [elem, setElem] = useState<T | undefined>(() =>
    selector ? (document.querySelector<T>(selector) ?? undefined) : undefined,
  )

  useEffect(() => {
    if (!selector) {
      setElem(undefined)
      return
    }
    // re-resolve for the current selector; setState bails out when the
    // element is unchanged, so the mount-time run after a sync hit is free
    const found = document.querySelector<T>(selector) ?? undefined
    setElem(found)
    if (found) return
    const t = querySelectorLazy<T>(selector, setElem)
    return () => {
      clearInterval(t)
    }
  }, [selector])

  return elem
}

type DragElementOptions = {
  target: HTMLElement
  activator?: HTMLElement
  isCenterByDefault?: boolean
}

/**
 * @todo switch to transform over left/top for performance?
 */
export function dragElement(options: DragElementOptions) {
  const { isCenterByDefault = true, activator, target } = options

  let p1 = 0
  let p2 = 0
  let p3 = 0
  let p4 = 0

  const _activator = activator || target
  const boundaries = {
    left: 0,
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
  }

  function onResize() {
    boundaries.right = window.innerWidth
    boundaries.bottom = window.innerHeight
  }

  function startDrag(e: MouseEvent) {
    e.preventDefault()
    p3 = e.clientX
    p4 = e.clientY
    window.addEventListener("mousemove", onDrag)
    window.addEventListener("mouseup", stopDrag)
  }

  function stopDrag(e: MouseEvent) {
    e.preventDefault()
    window.removeEventListener("mousemove", onDrag)
    window.removeEventListener("mouseup", stopDrag)
  }

  let left = 0
  let top = 0

  function onDrag(e: MouseEvent) {
    e.preventDefault()
    p1 = p3 - e.clientX
    p2 = p4 - e.clientY
    p3 = e.clientX
    p4 = e.clientY

    left = target.offsetLeft - p1
    top = target.offsetTop - p2

    const maxLeft = boundaries.right - target.offsetWidth
    const maxTop = boundaries.bottom - target.offsetHeight

    left = Math.max(boundaries.left, Math.min(left, maxLeft))
    top = Math.max(boundaries.top, Math.min(top, maxTop))

    target.style.left = `${left}px`
    target.style.top = `${top}px`
  }

  // @note this was an attempt to automatically handle off-screen rendering
  // issues when contents of the target element changes in size. The problem
  // is that it's not always a wanted behaviour to center the target in all
  // cases where the element changes size, such as Dialog isFullscreen / isDraggable
  // const obs = isCenterByDefault
  //   ? new ResizeObserver(centerTarget)
  //   : null

  function centerTarget() {
    top = window.innerHeight / 2 - target.offsetHeight / 2
    left = window.innerWidth / 2 - target.offsetWidth / 2
    target.style.top = `${top}px`
    target.style.left = `${left}px`
  }

  return {
    activate() {
      window.addEventListener("resize", onResize)
      window.addEventListener("mouseleave", stopDrag)
      _activator.addEventListener("mousedown", startDrag)
      if (isCenterByDefault) {
        // obs.observe(target)
        centerTarget()
      }
    },
    deactivate() {
      window.removeEventListener("resize", onResize)
      window.removeEventListener("mousemove", onDrag)
      window.removeEventListener("mouseup", stopDrag)
      window.removeEventListener("mouseleave", stopDrag)
      _activator.removeEventListener("mousedown", startDrag)
      // if(isCenterByDefault) obs.unobserve(target)
    },
  }
}

export function copyHtmlTableToPlainText(table: HTMLTableElement) {
  let text = ""

  for (let i = 0; i < table.rows.length; i++) {
    const row = table.rows[i]
    for (let j = 0; j < row.cells.length; j++) {
      const cell = row.cells[j]
      text += cell.textContent ? cell.textContent.trim() + "\t" : ""
    }
    text += "\n"
  }

  try {
    navigator.clipboard.writeText(text)
  } catch (error) {
    logError(error)
  }
}
